<template>
  <div>
    <p class="kl-tab-hint">
      {{ $t("teaching.instructionalMethod.statusesHint") }}
    </p>

    <div style="display: flex" class="mx-3">
      <div class="col p-0" style="text-align: left">
        <ButtonMedium
          v-can="'teaching.add_instructionalstatus'"
          v-if="allows_crud"
          :tooltip_text="
            $t('reusable.common.addTooltip', {
              entity: $t('teaching.instructionalMethod.statusEntity'),
            })
          "
          :text_button="$t('reusable.common.add')"
          :click_button="() => $bvModal.show('new-instructional-status-modal')"
          :icon="'plus'"
          :variant="'primary'"
        ></ButtonMedium>
      </div>
    </div>

    <!-- Misma tabla que la pestaña de Modelos: los estilos (encabezado azul,
         bordes, paginado, buscador) los pone GenericBTable, así las dos pestañas
         se ven igual sin duplicar CSS. -->
    <GenericBTable
      v-if="instructional_statuses.length > 0"
      :items="instructional_statuses"
      :fields="statusesFields"
      :filter="input_search"
      :filterCustom="filterCustom"
      @emitChangeSearchField="changeInputSearch"
      :show_pagination="true"
      :search_filter="true"
      :columns_display="true"
      :allows_crud="allows_crud"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
    >
      <template #cell(name)="row">
        <!-- El chip se ve tal como va a quedar la insignia en el diseño, para que
             el color se elija viendo el resultado y no a ciegas. -->
        <span class="kl-status-chip" :style="chipStyle(row.item)">
          {{ row.item.name }}
        </span>
      </template>

      <template #cell(is_active)="row">
        <b-icon
          :icon="row.item.is_active ? 'check-circle-fill' : 'slash-circle'"
          :variant="row.item.is_active ? 'success' : 'secondary'"
          v-b-tooltip.v-secondary.noninteractive="
            row.item.is_active
              ? $t('teaching.instructionalMethod.statusActive')
              : $t('teaching.instructionalMethod.statusActiveHint')
          "
        ></b-icon>
      </template>

      <template #cell(actions)="row">
        <template v-if="allows_crud">
          <ButtonSmall
            v-can="'teaching.change_instructionalstatus'"
            :tooltip_text="
              $t('reusable.common.editTooltip', {
                entity: $t('teaching.instructionalMethod.statusEntity'),
              })
            "
            :click_button="
              () => $bvModal.show(`edit-instructional-status-${row.item.id}`)
            "
            :class="`mr-1`"
            :icon="'square'"
          ></ButtonSmall>
          <ButtonSmall
            v-can="'teaching.delete_instructionalstatus'"
            :tooltip_text="$t('reusable.common.delete')"
            :click_button="() => askDelete(row.item)"
            :icon="'trash'"
          ></ButtonSmall>

          <b-modal
            :id="`edit-instructional-status-${row.item.id}`"
            :title="
              $t('reusable.common.editModalTitle', {
                entity: $t('teaching.instructionalMethod.statusEntity'),
              })
            "
            size="lg"
            hide-footer
            ignore-enforce-focus-selector="#sidebar-right"
          >
            <InstructionalStatusForm
              :InstructionalStatus="row.item"
              @saved="
                () => $bvModal.hide(`edit-instructional-status-${row.item.id}`)
              "
            ></InstructionalStatusForm>
          </b-modal>
        </template>
      </template>
    </GenericBTable>

    <div v-else class="mt-3">
      <strong>{{ $t("teaching.instructionalMethod.statusesEmpty") }}</strong>
    </div>

    <b-modal
      id="new-instructional-status-modal"
      :title="
        $t('reusable.common.createModalTitle', {
          entity: $t('teaching.instructionalMethod.statusEntity'),
        })
      "
      size="lg"
      hide-footer
      ignore-enforce-focus-selector="#sidebar-right"
      @show="create_key++"
    >
      <InstructionalStatusForm
        :key="create_key"
        :next_order="nextOrder"
        @saved="() => $bvModal.hide('new-instructional-status-modal')"
      ></InstructionalStatusForm>
    </b-modal>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";

export default {
  name: "InstructionalStatusTable",
  components: {
    GenericBTable: () => import("@/components/reusable/GenericBTable"),
    InstructionalStatusForm: () => import("./InstructionalStatusForm"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
  },
  props: {
    allows_crud: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      create_key: 0,
      input_search: "",
      sortBy: "order",
      sortDesc: false,
    };
  },
  computed: {
    ...mapGetters({
      instructional_statuses: names.INSTRUCTIONAL_STATUSES,
    }),
    statusesFields() {
      return [
        {
          key: "order",
          label: this.$t("teaching.instructionalMethod.statusOrder"),
          sortable: true,
          tdClass: "text-center",
          thClass: "text-center",
          display_column: true,
        },
        {
          key: "name",
          label: this.$t("reusable.common.name"),
          sortable: true,
          tdClass: "text-left",
          display_column: true,
        },
        {
          key: "is_active",
          label: this.$t("teaching.instructionalMethod.statusActive"),
          sortable: true,
          tdClass: "text-center",
          thClass: "text-center",
          display_column: true,
        },
        {
          key: "actions",
          label: "",
          display_column: true,
          thClass: "noprint",
          tdClass: "noprint text-center",
        },
      ];
    },
    nextOrder() {
      if (this.instructional_statuses.length === 0) return 1;
      return (
        Math.max(...this.instructional_statuses.map((x) => x.order || 0)) + 1
      );
    },
  },
  methods: {
    changeInputSearch(input_search) {
      this.input_search = input_search;
    },
    filterCustom(row) {
      return this.$filtered(String(row.name), this.input_search);
    },
    // Mismo criterio que los momentos: el texto se aclara u oscurece según el
    // color, para que el chip se lea con cualquier fondo.
    chipStyle(status) {
      const background_color = status.color || "#6b7b79";
      return {
        backgroundColor: background_color,
        color: this.$lightOrDark(background_color) ? "#2b2a2a" : "#ffffff",
      };
    },
    // El FK es SET_NULL: borrar un estado no borra nada del diseño, pero las piezas
    // que lo tenían quedan sin estado. Se avisa, porque desde acá no se ve cuántas
    // son y el cambio se nota recién al abrir el diseño.
    askDelete(status) {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle2", {
          entity: this.$t("teaching.instructionalMethod.statusEntity"),
          article: "el",
        }),
        text: this.$t("teaching.instructionalMethod.statusDeleteWarning"),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (!result.value) return;
        this.$store
          .dispatch(names.DELETE_INSTRUCTIONAL_STATUS, status.id)
          .catch(() => {
            toast(this.$t("teaching.instructional.saveError"));
          });
      });
    },
  },
  created() {
    this.$store.dispatch(names.FETCH_INSTRUCTIONAL_STATUSES);
  },
};
</script>

<style scoped>
.kl-tab-hint {
  font-size: var(--kl-fs-small);
  color: #6b7b79;
}
/* Identico al chip de momento de la pestaña de Modelos, con los mismos tokens de
   styles_kl.css: las dos pestañas de esta vista tienen que verse como una sola.
   La tabla en si la estiliza GenericBTable. */
.kl-status-chip {
  display: inline-block;
  border-radius: var(--kl-radius-sm, 4px);
  padding: 1px 6px;
  margin: 1px 2px;
  font-size: var(--kl-fs-caption);
}
</style>
